/**
 * B.12-R8 «Графики» tab. Переделка 26.08 (владелец):
 *  — операторы ЧИПАМИ (все сразу, без дропдауна); незаполнившие приглушены;
 *  — long-press по чипу скрывает оператора из списка (localStorage, личная
 *    настройка каждого ОО), кнопка «Показать всех» возвращает;
 *  — чип «ВСЕ» — мега-экран: каждая ячейка 14×17 разбита на точки по числу
 *    видимых операторов: зелёная=свободен, красная=занят, тусклая=не указал.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { api } from "../../api/client";
import type { BoardOperator } from "../../hooks/useOoDay";
import { useAllAvailability } from "../../hooks/useAllAvailability";
import { addDays, fmtDateApi } from "../../utils/date";
import { useClampPop } from "./useClampPop";
import { useChipSwipe } from "./useChipSwipe";
import { SchedCellPop } from "./SchedCellPop";
import { SchedDayExpand } from "./SchedDayExpand";
import css from "./board.module.css";

const HOURS = [10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 0, 1, 2];
const WD = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];
const HIDE_LS = "ub_sched_hidden";

export function SchedulesView({ operators, date }: { operators: BoardOperator[]; date: Date }) {
  const { data, loading } = useAllAvailability();
  const [sel, setSel] = useState<string>("ALL");
  const [hidden, setHidden] = useState<Set<string>>(() => {
    try { const v = JSON.parse(localStorage.getItem(HIDE_LS) || "[]") as unknown; return new Set(Array.isArray(v) ? v.map(String) : []); } catch { return new Set(); }
  });
  const filled = useMemo(() => new Set(Object.keys(data).filter((id) => Object.keys(data[id] ?? {}).length > 0)), [data]);
  const shown = operators.filter((o) => !hidden.has(o.id));
  const hideOp = (id: string) => setHidden((s) => {
    const n = new Set(s); n.add(id);
    try { localStorage.setItem(HIDE_LS, JSON.stringify([...n])); } catch { /* private mode */ }
    if (sel === id) setSel("ALL");
    return n;
  });
  /* смахивание чипа — скрыть (как и везде); тап — выбрать. Общий хук useChipSwipe. */
  const { swDown, swMove, swUp, swMoved } = useChipSwipe(hideOp);
  const showAll = () => { setHidden(new Set()); try { localStorage.removeItem(HIDE_LS); } catch { /* private mode */ } };
  const unhide = (id: string) => setHidden((s) => { const n = new Set(s); n.delete(id); try { localStorage.setItem(HIDE_LS, JSON.stringify([...n])); } catch { /* private mode */ } return n; });
  const [resetOpen, setResetOpen] = useState(false);
  const resetRef = useClampPop<HTMLDivElement>(resetOpen || null);
  useEffect(() => { if (!resetOpen) return; const onDown = (e: PointerEvent) => { if (!(e.target as HTMLElement | null)?.closest?.(`.${css.addOpWrap}`)) setResetOpen(false); }; document.addEventListener("pointerdown", onDown, true); return () => document.removeEventListener("pointerdown", onDown, true); }, [resetOpen]);

  const days = useMemo(() => Array.from({ length: 14 }, (_, i) => {
    const d = addDays(date, i);
    return { key: fmtDateApi(d), dow: WD[d.getUTCDay()] ?? "", num: d.getUTCDate() };
  }), [date]);

  /* 28.08 (владелец, v2): ОО не правит за оператора, а РАЗОВО открывает ему
     закреплённые дни — окно на 24 ч, оператор получает TG и правит сам. */
  const [unlocks, setUnlocks] = useState<Record<string, string>>({});
  const [unlockBusy, setUnlockBusy] = useState(false);
  const loadUnlocks = useCallback(() => {
    api<{ results?: { operator_id: string; expires_at: string }[] }>("/api/operator-availability/unlocks")
      .then((r) => setUnlocks(Object.fromEntries((r.results ?? []).map((u) => [u.operator_id, u.expires_at]))))
      .catch(() => {});
  }, []);
  useEffect(loadUnlocks, [loadUnlocks]);
  const toggleUnlock = async (opId: string) => {
    if (unlockBusy) return;
    setUnlockBusy(true);
    try {
      if (unlocks[opId]) await api(`/api/operator-availability/unlock?operator_id=${opId}`, { method: "DELETE" });
      else await api("/api/operator-availability/unlock", { method: "POST", body: { operator_id: opId } });
      loadUnlocks();
    } catch { /* показываем прежнее состояние */ }
    setUnlockBusy(false);
  };

  const one = sel !== "ALL" ? data[sel] ?? {} : null;
  /* транспонирование (дни сверху ⇄ часы сверху) + крест-подсветка тапом (26.08, владелец) */
  const [flip, setFlip] = useState(false);
  const [hl, setHl] = useState<{ d: string; h: number } | null>(null);
  /* 27.09 (владелец): тап по дате — раскрыть день: по часам кто занят (красный) и кто свободен (зелёный). */
  const [openDay, setOpenDay] = useState<string | null>(null);
  const stOf = (opId: string, dKey: string, h: number) => { const row = data[opId]?.[dKey] ?? {}; return row[h] ?? row[String(h)] ?? "none"; };
  /* 08.09 (владелец): long-press по ЛЮБОЙ ячейке — список операторов этой ячейки
     (зелёные — свободны, красные — заняты, серые — не отметили). Долгий тап не должен
     срабатывать как обычный клик (крест-подсветка) — гасим следующий click. */
  const [pop, setPop] = useState<{ d: string; h: number; x: number; y: number } | null>(null);
  const lp = useRef({ t: 0, x: 0, y: 0, fired: false });
  const press = (dKey: string, h: number) => ({
    onPointerDown: (e: { clientX: number; clientY: number }) => {
      lp.current = { ...lp.current, x: e.clientX, y: e.clientY, fired: false };
      window.clearTimeout(lp.current.t);
      lp.current.t = window.setTimeout(() => { lp.current.fired = true; setPop({ d: dKey, h, x: lp.current.x, y: lp.current.y }); }, 450);
    },
    onPointerMove: (e: { clientX: number; clientY: number }) => {
      if (Math.hypot(e.clientX - lp.current.x, e.clientY - lp.current.y) > 10) window.clearTimeout(lp.current.t);
    },
    onPointerUp: () => window.clearTimeout(lp.current.t),
    onPointerCancel: () => window.clearTimeout(lp.current.t),
  });
  const cell = (d: { key: string }, h: number, extra: string) => {
    const isHl = hl && (hl.d === d.key || hl.h === h);
    const isSel = hl && hl.d === d.key && hl.h === h;
    const cls = `${extra} ${isHl ? css.hlLine : ""} ${isSel ? css.hlCell : ""}`;
    const onClick = () => {
      if (lp.current.fired) { lp.current.fired = false; return; }
      setHl((p) => (p && p.d === d.key && p.h === h ? null : { d: d.key, h }));
    };
    if (one) {
      const st = one[d.key]?.[h] ?? one[d.key]?.[String(h)] ?? "none";
      return <div key={`${d.key}-${h}`} className={`${css.agCell} ${st === "free" ? css.cFree : st === "busy" ? css.cBusy : ""} ${cls}`} onClick={onClick} {...press(d.key, h)} />;
    }
    /* мега-режим: слои-«гамбургер» на всю ячейку (26.08 v2, владелец): порядок
       сверху вниз — серый (не отметили, если есть) → красный → ЗЕЛЁНЫЙ ВНИЗУ.
       Скрытые чипами операторы в расчёт не входят (shown). */
    let nF = 0, nB = 0, nN = 0;
    for (const o of shown) { const st = stOf(o.id, d.key, h); if (st === "free") nF++; else if (st === "busy") nB++; else nN++; }
    const total = Math.max(1, nF + nB + nN);
    return (
      <div key={`${d.key}-${h}`} className={`${css.agCell} ${css.agStack} ${cls}`} onClick={onClick} {...press(d.key, h)}
        title={`своб. ${nF} · зан. ${nB}${nN ? ` · не отм. ${nN}` : ""} — долгий тап: кто именно`}>
        {nN ? <i className={css.layNone} style={{ height: `${(nN / total) * 100}%` }} /> : null}
        {nB ? <i className={css.layBusy} style={{ height: `${(nB / total) * 100}%` }} /> : null}
        {nF ? <i className={css.layFree} style={{ height: `${(nF / total) * 100}%` }} /> : null}
      </div>
    );
  };
  return (
    <div className={css.content}>
      {/* чипы: тап — выбрать, long-press — скрыть из списка (личная настройка) */}
      <div className={css.schedChips}>
        <button type="button" className={`${css.schedChip} ${css.schedChipAll} ${sel === "ALL" ? css.on : ""}`} onClick={() => setSel("ALL")}>ВСЕ</button>
        {shown.map((o) => (
          <button key={o.id} type="button"
            className={`${css.schedChip} ${sel === o.id ? css.on : ""} ${filled.has(o.id) ? "" : css.dim}`}
            title={`${o.name}${filled.has(o.id) ? "" : " — график не заполнен"}; смахнуть — скрыть из списка`}
            onPointerDown={(e) => swDown(o.id, e)} onPointerMove={swMove}
            onPointerUp={swUp} onPointerCancel={swUp} onContextMenu={(e) => e.preventDefault()}
            onClick={() => { if (swMoved.current) { swMoved.current = false; return; } setSel(o.id); }}
          >{o.name}</button>
        ))}
        {sel !== "ALL" ? (
          <button type="button" className={`${css.schedChip} ${css.schedEdit} ${unlocks[sel] ? css.on : ""}`}
            title={unlocks[sel] ? "Окно правки открыто — тап, чтобы закрыть" : "Открыть оператору правку закреплённых дней на 24 ч (придёт уведомление)"}
            disabled={unlockBusy}
            onClick={() => void toggleUnlock(sel)}>
            {unlocks[sel] ? "🔓 открыт" : "🔓 открыть график"}
          </button>
        ) : null}
        {hidden.size ? (
          <span className={css.addOpWrap}>
            <button type="button" className={css.schedReset} title={`Скрытые операторы: ${hidden.size}`} onClick={() => setResetOpen((v) => !v)} />
            {resetOpen ? (
              <div className={css.mkOplist} ref={resetRef}>
                {operators.filter((o) => hidden.has(o.id)).map((o) => (
                  <button key={o.id} type="button" className={css.opItem} onClick={() => unhide(o.id)}><span className={css.opName}>{o.name}</span></button>
                ))}
                <button type="button" className={css.opItem} onClick={() => { showAll(); setResetOpen(false); }}><span className={css.opName}><b>Восстановить всех</b></span></button>
              </div>
            ) : null}
          </span>
        ) : null}
      </div>

      {loading ? (
        <div className={css.empty}>Загрузка графиков…</div>
      ) : !flip ? (
        <div className={`${css.availGrid} ${css.agTight} ${one ? "" : css.agHam}`} style={{ gridTemplateColumns: `30px repeat(${HOURS.length}, 1fr)` }} onContextMenu={(e) => e.preventDefault()}>
          <button type="button" className={css.agFlip} title="Поменять оси: даты сверху" onClick={() => { setFlip(true); setHl(null); }}>⇄</button>
          {HOURS.map((h) => <div key={`h${h}`} className={`${css.agHour} ${h < 10 ? css.night : ""} ${hl?.h === h ? css.hlHead : ""}`}>{String(h).padStart(2, "0")}</div>)}
          {days.map((d) => (
            <FragmentRow key={d.key}>
              <div className={`${css.agDay} ${css.agDayTap} ${hl?.d === d.key || openDay === d.key ? css.hlHead : ""}`} role="button" tabIndex={0}
                aria-expanded={openDay === d.key} title="Тап — кто занят и кто свободен по часам"
                onClick={() => setOpenDay((k) => (k === d.key ? null : d.key))}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpenDay((k) => (k === d.key ? null : d.key)); } }}>
                <span className={css.agDow}>{d.dow}</span><span className={css.agNum}>{d.num}</span>
              </div>
              {HOURS.map((h) => cell(d, h, h === 0 ? (css.cDivider ?? "") : ""))}
              {openDay === d.key ? <SchedDayExpand day={d} hours={HOURS} ops={shown} stOf={stOf} onClose={() => setOpenDay(null)} /> : null}
              {!one ? <div className={css.agDayGap} /> : null}
            </FragmentRow>
          ))}
        </div>
      ) : (
        <div className={`${css.availGrid} ${css.agTight}`} style={{ gridTemplateColumns: `30px repeat(${days.length}, 1fr)` }} onContextMenu={(e) => e.preventDefault()}>
          <button type="button" className={css.agFlip} title="Поменять оси: часы сверху" onClick={() => { setFlip(false); setHl(null); }}>⇄</button>
          {days.map((d) => <div key={`d${d.key}`} className={`${css.agHour} ${hl?.d === d.key ? css.hlHead : ""}`}>{d.dow}<br />{d.num}</div>)}
          {HOURS.map((h) => (
            <FragmentRow key={`r${h}`}>
              <div className={`${css.agDay} ${hl?.h === h ? css.hlHead : ""}`}><span className={css.agNum}>{String(h).padStart(2, "0")}</span></div>
              {days.map((d) => cell(d, h, ""))}
            </FragmentRow>
          ))}
        </div>
      )}
      {sel !== "ALL" && unlocks[sel] ? <div className={css.schedHint}>Оператору открыта правка закреплённых дней (до {unlocks[sel]!.slice(11, 16)} UTC). Он получил уведомление.</div>
        : !loading && sel !== "ALL" && !filled.has(sel) ? <div className={css.schedHint}>График не заполнен.</div> : null}
      {pop ? (
        <SchedCellPop
          at={pop}
          title={`${days.find((d) => d.key === pop.d)?.dow ?? ""} ${days.find((d) => d.key === pop.d)?.num ?? ""} · ${String(pop.h).padStart(2, "0")}:00`}
          ops={shown.map((o) => ({ id: o.id, name: o.name, st: stOf(o.id, pop.d, pop.h) }))}
          onClose={() => setPop(null)}
        />
      ) : null}
    </div>
  );
}

/** Плоская обёртка строк грида (grid не терпит вложенных div-обёрток). */
function FragmentRow({ children }: { children: ReactNode }) { return <>{children}</>; }
